import { api } from '../lib/api.js';
import type {
  CorrectionRequestInput,
  PunchRequest,
} from '@attendance/shared';

export interface AttendanceEvent {
  id: string;
  type: 'PUNCH_IN' | 'PUNCH_OUT' | 'BREAK_START' | 'BREAK_END';
  serverTimestamp: string;
  source: string;
  worksiteId: string | null;
  /** `type` is the WorksiteType enum string (e.g. 'CAMP' | 'CONSTRUCTION');
   *  it drives the camp/site journey labels and the "charged site" pick.
   *  The geometry is here so a punch can be drawn against the geofence it was
   *  actually judged by, and address/city/emirate give a punch a human place
   *  name without a reverse-geocode round trip. */
  worksite?: {
    id: string;
    name: string;
    type?: string;
    latitude?: number | null;
    longitude?: number | null;
    radiusMeters?: number | null;
    address?: string | null;
    city?: string | null;
    emirate?: string | null;
  } | null;
  status: string;
  notes: string | null;
  /** Proof-photo route id (the captured face image of a field punch); null when
   *  none was attached. Drives the per-punch proof thumbnail in the history views
   *  and the "No photo" badge when a face punch has no recoverable proof. */
  photoUrl?: string | null;
  /** 1:N face-match confidence (0–1) for a FACE punch; null otherwise. */
  faceMatchConfidence?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  /** GPS accuracy radius in metres. Only WEB punches report one — a FIELD
   *  punch carries coordinates but no accuracy, and kiosk/manual/system
   *  punches carry neither. Absent is normal, not an error. */
  accuracy?: number | null;
  /** The device reported a mocked/spoofed location. */
  isMockLocation?: boolean;
  /** The geofence verdict, as a code — `NO_GPS`, `NO_WORKSITE_MATCH`, or
   *  `OUTSIDE_GEOFENCE_AUDIT_ONLY[:metres:radius]`. Rendered through
   *  `useFlagReasonLabel()`, never printed raw. */
  flaggedReason?: string | null;
}

export interface PunchResult {
  eventId: string;
  type: 'PUNCH_IN' | 'PUNCH_OUT';
  serverTimestamp: string;
  worksite: { id: string; name: string };
  isLate: boolean;
  lateMinutes: number;
  /** Set when fraud detection or an out-of-geofence punch flagged the event. */
  flagged?: boolean;
  flagReason?: string | null;
  /** Selfie was provided but storage failed. Punch still recorded. */
  photoStorageDegraded?: boolean;
}

export interface AttendanceDay {
  id: string;
  date: string;
  status: string;
  totalWorkedMinutes: number;
  totalBreakMinutes: number;
  /** UNPAID camp<->site transit captured on the journey rollup. `transitOut`
   *  is camp->site (morning), `transitBack` is site->camp (evening). Never
   *  added to any paid/worked total — surfaced as a muted "unpaid" chip. */
  transitOutMinutes: number;
  transitBackMinutes: number;
  lateMinutes: number;
  earlyLeaveMinutes: number;
  overtimeMinutes: number;
  isHoliday: boolean;
  isWeekend: boolean;
  isOnLeave: boolean;
  firstPunchIn: string | null;
  lastPunchOut: string | null;
  /**
   * WHERE THE DAY WAS WORKED, most minutes first.
   *
   * Empty is a real answer and not a missing one: leave, a holiday, a weekend
   * and an absence all have no site, and a screen that drew those as "unknown
   * location" would be inviting a correction nobody needs to make (rule 3.4).
   * A punch that matched no site keeps a null name rather than being given one.
   *
   * Optional because an older API build does not send it — a client that
   * assumed the field would print `undefined` at a reader.
   */
  sites?: { worksiteId: string | null; worksiteName: string | null; workedMinutes: number }[];
}

export interface TodayStatus {
  employeeId: string;
  isCheckedIn: boolean;
  lastEvent: AttendanceEvent | null;
  events: AttendanceEvent[];
}

export interface PunchContext {
  employeeId: string;
  canPunch: boolean;
  reason: 'OK' | 'NO_EMPLOYEE' | 'INACTIVE' | 'NO_WORKSITE' | 'SOURCE_DISABLED';
  /**
   * The sites this punch may be matched against. `lat`/`lng` are NULLABLE and
   * must stay that way: the server has always been able to return an
   * address-only site, and this type claiming `number` is what let a null reach
   * the map and white-screen the whole page.
   */
  worksites: Array<{
    id: string;
    name: string;
    lat: number | null;
    lng: number | null;
    radiusMeters: number;
  }>;
  settings: {
    gpsAccuracyThresholdMeters: number;
    minPunchIntervalSeconds: number;
    requirePhotoWeb: boolean;
    /** True when this web check-in must capture + verify the employee's face. */
    requireFaceWeb: boolean;
    /** False when the worker's access mode / worksite allows punching with no GPS fix. */
    requireLocation: boolean;
  };
}

export interface Correction {
  id: string;
  employeeId: string;
  date: string;
  requestType: string;
  requestedTime: string;
  /** For a WRONG_TIME correction, the id of the punch event the request
   *  targets; the backend now returns it so a reviewer can see which punch a
   *  time change applies to. Absent for the other correction types. */
  targetEventId?: string;
  reason: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED' | 'CANCELLED';
  approvedById: string | null;
  approvedAt: string | null;
  rejectionReason: string | null;
  /** 'FIELD' when an operator raised this on a worker's behalf from the camp
   *  tablet/APK (vs a self-service web submission). Drives the "Field" badge so
   *  a reviewer can tell device-raised corrections from self-service ones. */
  source?: string | null;
  requestedById?: string | null;
  createdAt: string;
  employee?: {
    id: string;
    employeeCode: string;
    firstName: string;
    lastName: string;
    displayName: string | null;
  };
}

export const attendanceApi = {
  punch: (input: PunchRequest) =>
    api.post<{ data: PunchResult }>('/attendance/punch', input).then((r) => r.data),
  today: () => api.get<{ data: TodayStatus }>('/attendance/today').then((r) => r.data),
  punchContext: () =>
    api
      .get<{ data: PunchContext }>('/attendance/punch-context')
      .then((r) => r.data),
  events: (params: { from: string; to: string; employeeId?: string }) =>
    api.get<{ data: AttendanceEvent[] }>('/attendance/events', { params }).then((r) => r.data),
  days: (params: { from: string; to: string; employeeId?: string }) =>
    api.get<{ data: AttendanceDay[] }>('/attendance/days', { params }).then((r) => r.data),
  // Note: unlike sibling `days()` above, this one intentionally returns the
  // full envelope `{ data, meta }` rather than stripping to just `data`.
  // `api.get<T>()` already unwraps the AxiosResponse to `T`, so adding a
  // `.then((r) => r.data)` here would *re-strip* the body and drop `meta`.
  // The MyAttendance page reads `data.meta?.totals` (the `?.` handles the
  // case where the backend omits `meta` for empty ranges).
  daysWithTotals: (params: { from: string; to: string; employeeId?: string }) =>
    api.get<{ data: AttendanceDay[]; meta?: { totals: AttendanceDayTotals } }>(
      '/attendance/days',
      { params: { ...params, includeTotals: 'true' } }
    ),
};

export interface AttendanceDayTotals {
  totalWorkedMinutes: number;
  totalLateMinutes: number;
  totalOvertimeMinutes: number;
  totalEarlyLeaveMinutes: number;
  /**
   * Days carrying ANY late minutes — the population `totalLateMinutes` is the
   * sum of, and NOT the same set as `lateDays` (which counts the LATE day
   * STATUS, a set that excludes a late arrival on a half or incomplete day
   * because those statuses outrank LATE). State the two together and you get
   * "1 day (11h 42m)". Optional because an API build older than this field
   * simply omits it; the reader falls back to `lateDays`.
   */
  lateArrivalDays?: number;
  presentDays: number;
  lateDays: number;
  absentDays: number;
  onLeaveDays: number;
  holidayDays: number;
  weekendDays: number;
  incompleteDays: number;
  halfDays: number;
}

/**
 * The query key the corrections list is registered under, by filter.
 *
 * Two surfaces read the `pending` list: the approver's queue at
 * /corrections/approvals and the count badge on that row in the rail. Rule 3.6
 * (one event, one reason) applies to counts as much as to wording — a badge
 * that counts a queue must count the same ROWS that queue shows — and the only
 * way to guarantee that is for both to be one cache entry fed by one request,
 * rather than a second endpoint that scopes a MANAGER by its own rule.
 */
export const correctionsQueryKey = (filter: 'mine' | 'pending') =>
  ['corrections', filter] as const;

export const correctionsApi = {
  list: (filter?: 'mine' | 'pending') =>
    api
      .get<{ data: Correction[] }>('/corrections', { params: { filter } })
      .then((r) => r.data),
  submit: (input: CorrectionRequestInput) =>
    api.post<{ data: Correction }>('/corrections', input).then((r) => r.data),
  approve: (id: string) =>
    api.patch<{ data: Correction }>(`/corrections/${id}/approve`).then((r) => r.data),
  reject: (id: string, rejectionReason?: string) =>
    api
      .patch<{ data: Correction }>(`/corrections/${id}/reject`, { rejectionReason })
      .then((r) => r.data),
  cancel: (id: string) =>
    api.delete<{ data: Correction }>(`/corrections/${id}`).then((r) => r.data),
};
